import Food from "../data/food";
import React, { useContext, useEffect, useState } from "react";
import { CreateFoodDrawer } from "./CreateFoodDrawer";
import {
  Plan2PlateDrawer,
  Plan2PlateDrawerHeader,
  usePlan2PlateDisclosure,
} from "./Plan2PlateDrawer";
import { SearchInput } from "./SearchInput";
import { AddButton } from "./buttons/AddButton";
import { DrawerHistoryContext } from "../contexts";
import InfiniteScroll from "react-infinite-scroll-component";
import { useImmer } from "use-immer";
import { computeResultType, getMealSourceKey } from "../utils/mealSourceUtils";
import { MealSourceDto } from "../api";
import { useQueryClient } from "@tanstack/react-query";
import { ensureFoods, getFood } from "../queries/caches";
import { useNutriSlider } from "./nutriSlider";
import { usePerformSearch } from "../utils/searchUtils";
import { searchMealSource } from "../api";
import { DetailSearchResult, isRecent } from "./DetailSearchResult";

export function SearchForFoodDrawer(props: {
  onClose: () => void;
  open: boolean;
  selectFood: (food: Food) => void;
  initialSearchTerm?: string;
}) {
  const drawerHistoryContext = useContext(DrawerHistoryContext);
  const {
    isOpen: isOpenCreateFood,
    onOpen: onOpenCreateFood,
    onClose: onCloseCreateFood,
  } = usePlan2PlateDisclosure(drawerHistoryContext);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!props.open) {
      setSearchTerm("");
      updateSearchResults([]);
      setSearchResultCount(0);
    }
  }, [props.open]);

  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, updateSearchResults] = useImmer<MealSourceDto[]>([]);
  const [searchResultCount, setSearchResultCount] = useState(0);
  const nutriSliderProps = useNutriSlider("dish");
  const { performSearch, fetchMoreData } = usePerformSearch(
    undefined,
    nutriSliderProps,
    searchTerm,
    searchResults,
    updateSearchResults,
    setSearchResultCount,
    searchMealSource,
    10,
    ["FOOD"],
  );

  // Beim Öffnen mit einem Suchbegriff (z. B. aus dem Rezept-Import) direkt suchen.
  useEffect(() => {
    if (!props.open || !props.initialSearchTerm) return;
    setSearchTerm(props.initialSearchTerm);
    performSearch(true, props.initialSearchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  return (
    <>
      <CreateFoodDrawer
        onClose={onCloseCreateFood}
        open={isOpenCreateFood}
        onSubmit={(createdFood) => {
          props.selectFood(createdFood);
          props.onClose();
        }}
      />
      <Plan2PlateDrawer
        noScroll
        onClose={props.onClose}
        open={props.open}
        header={<Plan2PlateDrawerHeader heading={"Suche nach Lebensmitteln"} />}
        body={
          <>
            <div className="flex w-full items-center gap-2">
              <SearchInput
                dataCy="food-search-input"
                value={searchTerm}
                setSearchTerm={setSearchTerm}
                performSearch={performSearch}
                placeholder={"Suche nach Lebensmitteln"}
              />
              <AddButton onClick={onOpenCreateFood} />
            </div>
            <div
              id={"search-for-food-drawer-scroll-stack"}
              className="flex min-h-0 w-full flex-1 flex-col items-stretch overflow-auto"
            >
              <InfiniteScroll
                scrollableTarget={"search-for-food-drawer-scroll-stack"}
                dataLength={searchResults.length}
                next={fetchMoreData}
                hasMore={searchResults.length < searchResultCount}
                loader={<h4>Loading...</h4>}
                style={{ width: "100%" }}
              >
                <div className="flex w-full flex-col gap-2">
                  {searchResults.map((searchResult) => {
                    const indicator = searchResult.indicator;
                    return (
                      <DetailSearchResult
                        key={getMealSourceKey(searchResult)}
                        tags={searchResult.tags}
                        extraTags={searchResult.extraTags}
                        title={searchResult.description}
                        resultType={computeResultType(searchResult)}
                        recent={isRecent(searchResult)}
                        purchaseRelevant={searchResult.purchaseRelevant}
                        nutriValues={searchResult}
                        showNumbers={indicator == "DISH"}
                        onClick={async () => {
                          if (searchResult.id == null) {
                            return;
                          }
                          await ensureFoods(queryClient, [searchResult.id]);
                          const food = getFood(queryClient, searchResult.id);
                          if (!food) return;
                          props.selectFood(food);
                          props.onClose();
                        }}
                      />
                    );
                  })}
                </div>
              </InfiniteScroll>
            </div>
          </>
        }
      />
    </>
  );
}
